import {useApp} from "../../app.context";
import {useDrag} from "react-dnd";
import AssetImage from "../../elements/assetImage.element";
import {useIntegrator} from "../../integrator.context";
import {RemoteConstantsModel} from "../../models/remoteConstants.model";
import Checkmark from "../../elements/checkmark";
import {navTargetProps} from "../../elements/useNavTarget";
import {resolveEntityRef} from "../../utils/metadata.utils";

// Resolve through ImagePacks for buildings (their image lives at
// pack[startLevel][0], not under the bare uid). Units and tech-attribute
// unlocks key images by their own id, so they fall through unchanged.
// Exported because the Tech Tree canvas draws the same icons inside node
// circles and must resolve them identically.
export function resolveUnlockImageKey(state: RemoteConstantsModel, id: string): string {
    const buildingData = state.buildings.find(b => b.id === id);
    if (!buildingData) return id;
    const pack = state.imagePacks?.[id];
    if (!pack || pack.length === 0) return id;
    const level = Math.max(0, Math.min(pack.length - 1, buildingData.startLevel));
    return pack[level]?.[0] ?? id;
}

function TechUnlock(props: { nodeId: string, id: string, cancelDrag(): void }) {
    const {state} = useIntegrator();
    const {setNotification, setUnit, getTypeDescriptionKey, navigateTo} = useApp();

    const imageKey = resolveUnlockImageKey(state, props.id);

    const [{isDragging}, drag] = useDrag({
        type: 'entity',
        item: {
            nodeId: props.nodeId,
            id: props.id
        },
        end: () => {
            props.cancelDrag();
        },
        collect: (monitor) => {
            return {
                isDragging: monitor.isDragging(),
            };
        }
    });

    function onClick() {
        const unit = state.units.find(u => u.id === props.id);
        if (unit !== undefined) {
            setUnit(unit.id);
        } else {
            // Tech action/passive unlocks reference the metadata UID, but the
            // shared description is keyed by the metadata ID — resolve so the
            // popup shows/edits the same key as the Tech tab and the palette.
            // (Entity unlocks fall through: their ref IS the id.)
            const meta = [...(state.techActionAttributes ?? []), ...(state.techPassiveAttributes ?? [])]
                .find(m => m.uid === props.id || m.id === props.id);

            const price = getPrice();
            setNotification({
                // NotificationView treats title as a localization KEY (it
                // resolves it and, in edit mode, edits it) — pass the uid,
                // never the resolved display name.
                title: meta?.uid ?? props.id,
                message: meta ? getTypeDescriptionKey(meta.uid, meta.id) : `${props.id}.description`,
                edit: true,
                headerRight: price !== undefined && <div className={'layout horizontal center gap'}>
                    <p>{price}</p>
                    <AssetImage id={'gold'} size={30} background={'transparent'}/>
                </div>
            });
        }
    }

    function getPrice(): number | undefined {
        return state.buildings.find(b => b.id === props.id)?.price;
    }

    return (
        <div ref={drag}
             {...navTargetProps(navigateTo, resolveEntityRef(state, props.id))}
             style={{opacity: isDragging ? 0.5 : 1}}
             onClick={onClick}>
            <div style={{position: 'relative'}}>
                <AssetImage id={imageKey} size={30}/>
                <Checkmark id={props.id} style={{position: 'absolute', top: 0, right: 0}}/>
            </div>
        </div>
    )
}

export default TechUnlock;